'use client';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useDebounce } from 'use-debounce';
import useGlobalSearch from 'src/hooks/globalSearch/useGlobalSearch';
// Import directly from hook file to avoid circular dependency via hooks/index.ts
import { useGenericComponent } from '../../hooks/useGenericComponent';
import { GenericSelectorModal, GenericNodeComponent, ConnectorType } from '../shared';
import {
  ProviderItem,
  providerModalConfig,
  providerNodeConfig,
  transformProvider,
} from './config';

interface ProviderComponentProps {
  componentId: string;
  initialProviders?: ProviderItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

// Extract IDs for Redux storage
const getItemIds = (items: ProviderItem[]) => items.map((p) => p.provider_id);

const ProviderComponent: React.FC<ProviderComponentProps> = ({
  componentId,
  initialProviders = [],
  initialConnector = 'ANY',
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Use generic component hook for state management
  const {
    items: providers,
    connector,
    showModal,
    searchTerm,
    setShowModal,
    setSearchTerm,
    handleSave,
    handleModalClose,
    handleRemoveItem,
    handleConnectorChange,
  } = useGenericComponent<ProviderItem>({
    componentId,
    type: 'providers',
    reduxDataField: 'value',
    getItemIds,
    initialItems: initialProviders,
    initialConnector,
    onUpdate,
    onRemove,
    fromPlaceholder,
  });

  // Debounce search term
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Fetch search results using global search
  const { data: searchData, isLoading } = useGlobalSearch(debouncedSearch);

  // Transform API results to standard format
  const searchResults: ProviderItem[] = (searchData || []).map(transformProvider);

  return (
    <>
      {providers.length > 0 ? (
        <GenericNodeComponent
          config={providerNodeConfig}
          items={providers}
          connector={connector}
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
          onConnectorChange={handleConnectorChange}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-full cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => setShowModal(true)}
        >
          <span className="text-blue-600 text-xs font-medium">
            Select providers...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={providerModalConfig}
        isOpen={showModal}
        selectedItems={providers}
        connector={connector}
        isEditing={providers.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default ProviderComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderProviderComponent = (
  container: HTMLElement,
  props: ProviderComponentProps & {
    initialProviders?: ProviderItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <ProviderComponent {...props} />
    </Provider>
  );
  return root;
};
